import {
  API_PREFIX_JAVA,
  API_PREFIX_JAVA_REDIRECT,
  API_REDIRECT_LIST,
  REGEXP_API_TRANSFER,
} from "@/constant";
import { request as umiRequest } from "@umijs/max";
import type { YsResponse } from "@/types";
import { removeFalsy } from "@/utils/help";
import { generateSign } from "@/utils/sign";
import type { ResponseType } from "axios";
import type { RequestFunctionParams } from "yapi-to-typescript";
import qs from "qs";

export interface RequestOptions {
  /**
   * 使用的服务器。
   *
   * - `prod`: 生产服务器
   * - `dev`: 测试服务器
   * - `mock`: 模拟服务器
   *
   * @default prod
   */
  server?: "prod" | "dev" | "mock";
  /**
   * 是否启用驼峰转下划线
   * eg：pageSize => page_size
   */
  snakeCase?: boolean;
  showMessage?: boolean;
  responseType?: ResponseType;
}

/**
 * 生成 formData 数据
 */
const generateFormData = (target: Record<string, string>): FormData => {
  const formData = new FormData();
  if (target) {
    Object.keys(target).forEach((key) => {
      if (
        ["Array", "Object"].includes(
          Object.prototype.toString.call(target[key]).slice(8, -1),
        )
      ) {
        formData.append(key, JSON.stringify(target[key]));
      } else {
        formData.append(key, target[key]);
      }
    });
  }
  return formData;
};

export default function request<TResponseData>(
  payload: RequestFunctionParams,
  ysOptions: RequestOptions = {
    snakeCase: true,
    showMessage: true,
    responseType: "json",
  },
): Promise<YsResponse<TResponseData>> {
  const { method, path, rawData } = payload;
  // 当前时间戳
  const timestamp = Math.floor(Date.now() / 1000);
  // 最终请求路径
  let finalPath = path;
  const requestData =
    method === "GET"
      ? { ...qs.parse(path.split("?")[1]), timestamp }
      : { ...(rawData || {}), timestamp };
  const trulyData = removeFalsy(requestData);
  const sign = generateSign(trulyData);
  const signData = { ...trulyData, sign };
  const formData = generateFormData(signData);
  // Java 端转发接口接收参数一定要 formData 形式，不支持json
  payload.requestHeaders = { "Content-Type": "multipart/form-data" };
  payload.data = formData;
  if (payload.method === "GET") {
    finalPath = `${path}${
      /\?/.test(path) ? "&" : "?"
    }timestamp=${timestamp}&sign=${sign}`;
  }
  // 商城环境增加接口前缀
  if (API_REDIRECT_LIST.some((item) => path.startsWith(item))) {
    // 如果是下载上传类，直接通过java透传转发
    finalPath = `${API_PREFIX_JAVA_REDIRECT}${path}`;
  } else if (REGEXP_API_TRANSFER.test(path)) {
    // 普通接口，直接通过java转发
    finalPath = `${API_PREFIX_JAVA}${path}`;
  }
  return umiRequest<YsResponse<TResponseData>>(finalPath, {
    ...payload,
    ...ysOptions,
  });
}
